import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { ApiService } from '../../api';
import { PaginatedResponse, SortOrder } from '../../common/entities/paginated-response';
import { ArrivalBoardConfigService } from './arrival-board-config.service';
import { ArrivalBoardConfiguration } from '../entities/arrival-board-configuration';
import { joinUrl } from './join-url';

/** Path used when the HAL root does not advertise the configuration link. */
const CONFIGURATIONS_PATH = 'arrival-board/configurations';

/** The editor lists every board in the user's groups, and the server default limit is only 10. */
const LIST_LIMIT = 100;

/**
 * Sortable columns of the configuration list. The backend expects the **enum key**
 * (`NAME`), not the column name, so the values here are the keys themselves.
 */
export enum ArrivalBoardConfigSortBy {
    UUID = 'UUID',
    NAME = 'NAME',
    GROUP_PATH = 'GROUP_PATH',
    QUESTIONNAIRE = 'QUESTIONNAIRE',
    CREATED_AT = 'CREATED_AT',
    UPDATED_AT = 'UPDATED_AT',
}

/**
 * HTTP-backed store for ArrivalBoard configurations.
 *
 * Two deliberate differences from the local mock:
 *
 * - **`default` is not de-duplicated server-side.** The backend happily persists several default
 *   boards, so clearing the flag on siblings is an editor-side courtesy performed here, after a
 *   successful save.
 * - **The server clamps `pollingInterval`** up to its own minimum, so the submitted object is not
 *   authoritative — every write returns what was actually persisted.
 */
@Injectable()
export class HttpArrivalBoardConfigService extends ArrivalBoardConfigService {
    private readonly http = inject(HttpClient);
    private readonly apiService = inject(ApiService);

    getConfigs(
        sortBy: ArrivalBoardConfigSortBy = ArrivalBoardConfigSortBy.NAME,
        sortOrder: SortOrder = SortOrder.ASC,
    ): Observable<ArrivalBoardConfiguration[]> {
        const params = new HttpParams()
            .set('limit', LIST_LIMIT)
            .set('offset', 0)
            .set('sortBy', sortBy)
            .set('sortOrder', sortOrder);
        return this.http
            .get<PaginatedResponse<ArrivalBoardConfiguration[]>>(this.configurationsUrl(), { params })
            .pipe(map((response) => response?.items ?? []));
    }

    getConfig(uuid: string): Observable<ArrivalBoardConfiguration | undefined> {
        return this.http.get<ArrivalBoardConfiguration>(`${this.configurationsUrl()}/${uuid}`);
    }

    /**
     * No backend endpoint exists for this: the flag is not de-duplicated server-side, so the pick
     * is made deterministic here instead. Editor-only — the kiosk resolves its board by id.
     */
    getDefaultConfig(): Observable<ArrivalBoardConfiguration | undefined> {
        return this.getConfigs().pipe(map((configs) => configs.find((config) => config.default) ?? configs[0]));
    }

    create(config: ArrivalBoardConfiguration): Observable<ArrivalBoardConfiguration> {
        return this.http
            .post<ArrivalBoardConfiguration>(this.configurationsUrl(), writeBody(config))
            .pipe(switchMap((created) => this.clearSiblingDefaults(created)));
    }

    update(config: ArrivalBoardConfiguration): Observable<ArrivalBoardConfiguration> {
        return this.http
            .put<ArrivalBoardConfiguration>(`${this.configurationsUrl()}/${config.uuid}`, writeBody(config))
            .pipe(switchMap((updated) => this.clearSiblingDefaults(updated)));
    }

    delete(uuid: string): Observable<void> {
        return this.http.delete<void>(`${this.configurationsUrl()}/${uuid}`);
    }

    /**
     * Courtesy single-default enforcement, mirroring what the local mock did in memory. Runs only
     * after a successful save, so a rejected write cannot clear another board's flag.
     *
     * @param saved - the persisted resource, as returned by the server
     */
    private clearSiblingDefaults(saved: ArrivalBoardConfiguration): Observable<ArrivalBoardConfiguration> {
        if (!saved?.default) return of(saved);
        return this.getConfigs().pipe(
            switchMap((configs) => {
                const siblings = configs.filter((config) => config.default && config.uuid !== saved.uuid);
                if (!siblings.length) return of(saved);
                return forkJoin(
                    siblings.map((sibling) =>
                        this.http.put<ArrivalBoardConfiguration>(
                            `${this.configurationsUrl()}/${sibling.uuid}`,
                            writeBody({ ...sibling, default: false }),
                        ),
                    ),
                ).pipe(map(() => saved));
            }),
        );
    }

    /**
     * Resolved per call, never in a field initializer: a field would freeze the URL at construction
     * time, which is before `ApiService.init()` has necessarily resolved the API root.
     */
    private configurationsUrl(): string {
        const href = this.apiService.rootResponseSignal()?._links?.arrivalBoardConfiguration?.href;
        return joinUrl(this.apiService.urlSignal(), href ?? CONFIGURATIONS_PATH);
    }
}

/**
 * Body of a create or update, listing the DTO's properties explicitly.
 *
 * Necessary rather than cosmetic: the backend validates with `forbidNonWhitelisted`, so any extra
 * property is a hard `400`. A persisted resource carries `_links`, `created_at` and `updated_at`,
 * which rules out spreading one back — and the sibling-clearing PUT below does exactly that, with an
 * object that came straight from the list endpoint. `uuid` is omitted too: it is already in the path,
 * and whether the update DTO accepts it in the body is not part of the contract.
 *
 * `alerts` is the one field here whose **empty array is meaningful**, so it is always written — see the
 * comment on it below.
 *
 * @param config - the configuration to write, persisted or editor-built
 */
function writeBody(config: ArrivalBoardConfiguration): Record<string, unknown> {
    return {
        name: config.name,
        groupPath: config.groupPath,
        default: config.default,
        pollingInterval: config.pollingInterval,
        questionnaire: config.questionnaire,
        columns: config.columns,
        // Unconditional, `[]` included, and deliberately not `...(config.alerts?.length && { alerts })`:
        // the backend update is a partial merge (`if (dto.alerts !== undefined) existing.alerts = …`), so
        // an omitted `alerts` means *unchanged*, not *cleared*. Under a truthiness guard the user who
        // deletes their last rule gets a 200 and the rule back on the next reload, forever, with nothing
        // reported anywhere — a quiet data-loss bug traded for a loud one. Every other field here is
        // either always set or harmless to resend.
        alerts: config.alerts,
    };
}
